import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import ContactForm from '../../Component/ContactForm';
import ContactEditForm from '../../Component/contacts/EditContact';
import ContactTable from '../../Component/contacts/ContactTable';
import SelectedPopup from '../../Component/common/selectedPopup';
import Header from '../../Component/common/Header/Header';
import SearchBar from '../../Component/common/SearchBar/SearchBar'; 
import ExportButton from '../../Component/common/ActionButton/ExportButton';
import Filter from '../../Component/common/Filter/Filter';
import useStatusOptions from '../../hooks/useStatusOptions';
import useSourceOptions from '../../hooks/useSourceOptions';
import ColumnSelectorModal from '../../Component/common/ColumnSelectorModal';
import * as XLSX from 'xlsx';
import './ContactsPage.css';

const ContactsPage = () => {
  const { statusOptions, loading: statusLoading, error: statusError } = useStatusOptions();
  const { sourceOptions, loading: sourceLoading, error: sourceError } = useSourceOptions();
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const fetchContacts = async () => {
    try {
      const response = await apiClient.get('/api/contacts');
      console.log('contacts ', response.data.slice(0, 5));
      setContacts(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (contacts.length > 0) {
      // Initialize selected columns with all keys of the first contact
      setSelectedColumns(Object.keys(contacts[0]));
    }
  }, [contacts]);

  const handleNewContact = (newContact) => {
    setContacts([...contacts, newContact]);
    setShowForm(false);
  };

  const handleUpdatedContact = (updatedContact) => {
    setContacts(contacts.map(contact =>
      contact.id === updatedContact.id ? updatedContact : contact
    ));
    setEditingContact(null);
  };

  const handleContactClick = async (contactId) => {
    try {
      const response = await apiClient.get(`/api/contacts/${contactId}`);
      setSelectedContact(response.data);
    } catch (error) {
      console.error('Error fetching contact details:', error);
    }
  };

  const handleEditClick = (contactId) => {
    const contactToEdit = contacts.find(c => c.id === contactId);
    console.log('contact to edit', contactToEdit);
    setEditingContact(contactToEdit);
  };

  const filteredContacts = contacts.filter(contact =>
    contact &&
    (
      (selectedType === 'all' || contact.contact_type === selectedType) &&
      (selectedSource === 'all' || (contact.source && contact.source.toLowerCase().trim() === selectedSource.toLowerCase().trim())) &&
      (selectedStatus === 'all' || (contact.status && contact.status.toLowerCase().trim() === selectedStatus.toLowerCase().trim())) &&
      (
        contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone?.includes(searchTerm)
      )
    )
  );

  const handleWhatsAppClick = (phoneNumber) => {
    if (!phoneNumber) {
      alert('No phone number available for this contact');
      return;
    }
    const cleanedPhoneNumber = phoneNumber.replace(/\\D/g, '');
    const whatsappUrl = `https://wa.me/${cleanedPhoneNumber}`;
    window.open(whatsappUrl, '_blank');
  };

  const updateContactRole = async (contactId, roleType, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`/api/contacts/${contactId}/mark-as-${roleType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to mark as ${roleType}`);
      }

      await fetchContacts();
    } catch (error) {
      console.error(`Error marking as ${roleType}:`, error);
      alert(error.message || `Failed to mark as ${roleType}`);
    }
  };

  const addToTodo = async (contact) => {
    try {
      const response = await fetch('/api/todos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: contact.name || '',
          number: contact.phone || '',
          productName: '',
          status: 'pending',
          targetDate: new Date().toISOString().split('T')[0],
          source: 'contacts'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to add Contact ${contact.name} to todo`);
      }
      alert(`Contact ${contact.name} added to todo list.`);
      await fetchContacts();
    } catch (error) {
      console.error(`Error adding Contact ${contact.name} to todo:`, error);
      alert(error.message || `Failed to add Contact ${contact.name} to todo`);
    }
  };

  const exportToExcel = () => {
    if (selectedColumns.length === 0) {
      alert('Please select at least one column to export.');
      return;
    }
    const dataToExport = filteredContacts.map(contact => {
      const filteredData = {};
      selectedColumns.forEach(col => {
        filteredData[col] = contact[col];
      });
      return filteredData;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
    XLSX.writeFile(workbook, 'contacts_export.xlsx');
    setShowColumnSelector(false);
  };

  return (
    <div className="contacts-page">
      <Header
        title="All Contacts"
        onAddAction={() => setShowForm(true)}/>
      <div className="filter-row">
        <SearchBar
          placeholder="Search contacts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button
          type="button"
          className="btn add-contact-button"
          onClick={() => setShowForm(true)}
          title="Add Contact"
        >
          Add Contact
        </button>
        <ExportButton
          title="Export Contacts"
          onClick={() => setShowColumnSelector(true)}
          className="btn export-button-margin"
        />
      </div>
      <div className="filter-row">
        <Filter
          label="Filter by Type:"
          id="typeFilter"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          options={[
            { value: 'all', label: 'All' },
            { value: 'manufacturer', label: 'Manufacturer' },
            { value: 'buyer', label: 'Buyer' },
            { value: 'contractor', label: 'Contractor' },
          ]}
        />
        <Filter
  label="Filter by Source:"
  id="sourceFilter"
  value={selectedSource}
  onChange={(e) => setSelectedSource(e.target.value)}
  options={[
    { value: 'all', label: 'All' },
    ...[...new Set(
      contacts
        .map(c => c.source?.trim())
        .filter(source => source && source.length > 0)
    )].map(src => ({
      value: src,
      label: src
    }))
  ]}
/>
        <Filter
          label="Filter by Status:"
          id="statusFilter"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          options={[
            { value: 'all', label: 'All' },
            ...statusOptions.map(status => ({ value: status, label: status }))
          ]}
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading contacts...</div>
      ) : (
        <div className="contacts-list">
          {filteredContacts.length > 0 ? (
            <ContactTable
              contacts={filteredContacts}
              onContactClick={handleContactClick}
              onEditClick={handleEditClick}
              onWhatsAppClick={handleWhatsAppClick}
              onMarkAsManufacturer={(id, e) => updateContactRole(id, 'manufacturer', e)}
              onMarkAsBuyer={(id, e) => updateContactRole(id, 'buyer', e)}
              onMarkAsContractor={(id, e) => updateContactRole(id, 'contractor', e)}
              refreshContacts={fetchContacts}
              addToTodo={addToTodo}
            />
          ) : (
            <div className="no-contacts">
              {searchTerm ? 'No matching contacts found' : 'No contacts available'}
            </div>
          )}
        </div>
      )}

      {/* Add Contact Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Contact</h2>
              <button onClick={() => setShowForm(false)} className="close-btn">
                &times;
              </button>
            </div>
            <ContactForm onSuccess={handleNewContact} />
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Contact</h2>
              <button
                onClick={() => setEditingContact(null)}
                className="close-btn"
              >
                &times;
              </button>
            </div>
            <ContactEditForm
              contact={editingContact}
              statusOptions={statusOptions}
              statusLoading={statusLoading}
              statusError={statusError}
              sourceOptions={sourceOptions}
              sourceLoading={sourceLoading}
              sourceError={sourceError}
              onSuccess={(updatedContact) => {
                handleUpdatedContact(updatedContact);
                setEditingContact(null);
              }}
              onCancel={() => setEditingContact(null)}
            />
          </div>
        </div>
      )}

      {/* Contact Details Modal */}
      {selectedContact && (
        <SelectedPopup
          selectedItem={selectedContact}
          activityLogs={[]}   //You can fetch and pass actual activity logs if available 
          closePopup={() => setSelectedContact(null)}
          handleEditClick={(contact) => {
            setEditingContact(contact);
            setSelectedContact(null);
          }}
          itemType="contact"
        />
      )}

      {showColumnSelector && (
        <ColumnSelectorModal
          columns={contacts.length > 0 ? Object.keys(contacts[0]) : []}
          selectedColumns={selectedColumns}
          onClose={() => setShowColumnSelector(false)}
          onConfirm={(cols) => {
            setSelectedColumns(cols);
            exportToExcel();
          }}
        />
      )}
    </div>
  );
};

export default ContactsPage;
